import { Component, OnInit, ViewChild } from '@angular/core';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { DataFieldGroup } from '@models/settings/data-field-group.model';
import { AnalyticService, AuthService, NotifyService } from '@app/services';
import { fieldLocationToComponent } from '@import/enums/custom-field-locations.enum';
import { ConfirmationDialogComponent } from '@app/components';
import { FieldGroupsService } from '@settings/services';
import { AnalyticEvents } from '@app/enums';
import { Translatable } from '@app/types/translatable.type';
import { TranslateService } from '@ngx-translate/core';

@Component({
    templateUrl: './field-groups-ordering.template.html',
    styleUrls: ['./field-groups-ordering.style.scss'],
})
export class FieldGroupsOrderingView implements OnInit {
    @ViewChild('groupMovementConfirmDialog', { static: true }) groupMovementConfirmDialog: ConfirmationDialogComponent;
    isLoading = true;
    title = 'settings.custom-fields.reorderFieldGroups';
    groupMovementConfirmHeader: Translatable = 'settings.custom-fields.confirmMove';
    fieldLocationToComponent = fieldLocationToComponent;
    customPersonalGroups = [];
    customJobAndPayGroups = [];
    defaultPersonalGroups = [];
    defaultJobAndPayGroups = [];
    confirmationModalMessages = {
        [fieldLocationToComponent.personalCustomFields]: [
            'settings.custom-fields.confirmationPersonalCustomFields1',
            'settings.custom-fields.confirmationPersonalCustomFields2',
        ],
        [fieldLocationToComponent.jobAndPayCustomFields]: [
            'settings.custom-fields.confirmationJobAndPayCustomFields1',
            'settings.custom-fields.confirmationJobAndPayCustomFields2',
        ],
    };
    movingGroupTo = fieldLocationToComponent.personalCustomFields;

    constructor(
        public auth: AuthService,
        private analyticsService: AnalyticService,
        private notify: NotifyService,
        private fieldGroupService: FieldGroupsService,
        private translateService: TranslateService
    ) {}

    async ngOnInit(): Promise<void> {
        await this.fetchDataFieldGroups();
        this.isLoading = false;
    }

    /*
     * This method is responsible for moving groups within a list or to sibling list.
     * If a group is being moved to a sibling list we want to confirm so we'll move the group
     * temporarily, but if the confirmation returns false we will move the group back.
     * */
    async drop(event: CdkDragDrop<DataFieldGroup[]>, location: fieldLocationToComponent): Promise<void> {
        if (event.previousContainer === event.container) {
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
            return;
        }

        transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);

        const tab =
            location === 'personal'
                ? 'settings.custom-fields.defaultFieldGroupLabels.personal'
                : 'settings.custom-fields.defaultFieldGroupLabels.job_and_pay';
        this.movingGroupTo = location;
        const movedGroup = event.container.data[event.currentIndex];
        this.groupMovementConfirmHeader = {
            key: 'settings.custom-fields.groupMoveConfirmation',
            params: {
                group: this.translateService.instant(movedGroup.translatableName),
                tab: this.translateService.instant(tab),
            },
        };

        const shouldMoveGroup = await this.groupMovementConfirmDialog.open();

        if (!shouldMoveGroup) {
            transferArrayItem(
                event.container.data,
                event.previousContainer.data,
                event.currentIndex,
                event.previousIndex
            );
        }
    }

    async onSave(): Promise<void> {
        this.isLoading = true;

        try {
            await this.updateLocationOfGroups();
            await this.updateOrderingOfGroups();
            this.analyticsService.trackEvent(AnalyticEvents.SettingsModuleReorderCustomFieldGroups);
            this.notify.success('settings.custom-fields.customFieldsSuccessUpdated');
        } catch (e) {
            this.notify.error('settings.custom-fields.customFieldsNotUpdated');
        }

        this.isLoading = false;
    }

    private async fetchDataFieldGroups(): Promise<void> {
        const [[customPersonalGroups], [customJobAndPayGroups], [defaultPersonalGroups], [defaultJobAndPayGroups]] =
            await Promise.all([
                DataFieldGroup.param('company', this.auth.company.id)
                    .where('component', fieldLocationToComponent.personalCustomFields)
                    .where('onlyCustomGroups', true)
                    .orderBy('orderBy', 'ASC')
                    .all(),
                DataFieldGroup.param('company', this.auth.company.id)
                    .where('component', fieldLocationToComponent.jobAndPayCustomFields)
                    .where('onlyCustomGroups', true)
                    .orderBy('orderBy', 'ASC')
                    .all(),
                DataFieldGroup.param('company', this.auth.company.id)
                    .where('component', fieldLocationToComponent.personalCustomFields)
                    .where('onlyDefaultGroups', true)
                    .all(),
                DataFieldGroup.param('company', this.auth.company.id)
                    .where('component', fieldLocationToComponent.jobAndPayCustomFields)
                    .where('onlyDefaultGroups', true)
                    .all(),
            ]);

        this.customPersonalGroups = customPersonalGroups;
        this.customJobAndPayGroups = customJobAndPayGroups;
        this.defaultPersonalGroups = defaultPersonalGroups;
        this.defaultJobAndPayGroups = defaultJobAndPayGroups;
    }

    private async updateLocationOfGroups(): Promise<void> {
        const personalGroupIds = this.customPersonalGroups.map((group) => group.id);
        const jobAndPayGroupIds = this.customJobAndPayGroups.map((group) => group.id);
        const promises = [];

        if (personalGroupIds.length) {
            promises.push(
                this.fieldGroupService.bulkUpdateComponent(
                    personalGroupIds,
                    fieldLocationToComponent.personalCustomFields
                )
            );
        }
        if (jobAndPayGroupIds.length) {
            promises.push(
                this.fieldGroupService.bulkUpdateComponent(
                    jobAndPayGroupIds,
                    fieldLocationToComponent.jobAndPayCustomFields
                )
            );
        }

        await Promise.all(promises);
    }
    private async updateOrderingOfGroups(): Promise<void> {
        const personalGroupIds = this.customPersonalGroups.map((group) => group.id);
        const jobAndPayGroupIds = this.customJobAndPayGroups.map((group) => group.id);
        const promises = [];

        if (personalGroupIds.length) {
            promises.push(
                this.fieldGroupService.bulkUpdateOrder(personalGroupIds, fieldLocationToComponent.personalCustomFields)
            );
        }
        if (jobAndPayGroupIds.length) {
            promises.push(
                this.fieldGroupService.bulkUpdateOrder(
                    jobAndPayGroupIds,
                    fieldLocationToComponent.jobAndPayCustomFields
                )
            );
        }

        await Promise.all(promises);
    }
}
